<template>
  <div class="p-6 bg-white border rounded-md border-slate-200 drop-shadow-sm">
    <div class="flex items-center justify-between w-full mb-2 gap-x-6">
      <div class="items-center">
        <p class="font-semibold">
          Pollutant Details
        </p>
        <span
          v-if="selectedPollutant.name"
          class="text-xs"
        >{{ selectedPollutant.name }} ({{
          selectedPollutant.chemical_formula
        }})</span>
      </div>
      <div class="flex items-center justify-end float-right">
        <span class="mt-2.5 text-xs text-gray-400">as of {{ $moment().format("DD MMM YYYY, HH:mm") }}</span>
      </div>
    </div>
    <section class="container max-w-screen-lg mx-auto text-center hero">
      <div
        v-if="selectedPollutant.name"
        class="grid grid-cols-1 sm:grid-cols-2"
        :class="{ 'md:grid-cols-3': !noEmissions }"
      >
        <div
          v-if="!noEmissions"
          class="flex h-[180px] flex-col items-center justify-center gap-4"
        >
          <ve-progress
            :progress="Number(footprintPercentage)"
            color="#30BE2E"
            empty-color="#F2F2F2"
            :size="90"
            :thickness="8"
            :empty-thickness="8"
            legend
            :legend-value="footprintPercentage"
            font-size="1.4rem"
            font-color="black"
          >
            <template #legend-value>
              <span class="mr-1">%</span>
            </template>
          </ve-progress>
          <span class="text-sm text-[#595757]">of total emissions</span>
        </div>
        <div class="flex h-[180px] flex-col items-center justify-center gap-4">
          <ve-progress
            :progress="gwpPercentage"
            color="#30BE2E"
            empty-color="#F2F2F2"
            :size="90"
            :thickness="8"
            :empty-thickness="8"
            legend
            :legend-value="formattedGwp"
            :font-size="selectedPollutant.gwp > 1000 ? '1rem' : '1.4rem'"
            font-color="black"
          />
          <span class="text-sm text-[#595757]">GWP (100 years)</span>
        </div>
        <div class="flex h-[180px] flex-col items-center justify-center gap-4">
          <apex-chart
            ref="pollutantTrendChart"
            width="100%"
            height="50%"
            :options="defaultPollutantTrendChartOptions"
            :series="defaultPollutantTrendChartSeries"
          />
          <div class="relative flex items-center gap-1 text-sm text-[#595757]">
            <span>From the previous
              {{ selectedPollutantTrend.replace("ly", "") }}</span>
            <button
              type="button"
              class="text-gray-400 outline-none"
              @click="showTrendList = !showTrendList"
            >
              <span v-if="!showTrendList">
                <ChevronDownSmall />
              </span>
              <span v-else>
                <ChevronUpSmall />
              </span>
            </button>
            <ul
              v-if="showTrendList"
              role="listbox"
              class="absolute right-0 px-4 py-2 text-left bg-white rounded-md shadow-md top-full text-slate-900"
            >
              <li
                v-for="(option, index) in pollutantTrendOptions"
                :key="index"
                class="cursor-pointer first-letter:capitalize hover:text-green-600"
                :class="{ 'text-green-600': option === selectedPollutantTrend }"
                @click="
                  selectedPollutantTrend = option;
                  showTrendList = false;
                "
              >
                {{ option }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div v-else>
        <NuxtImg
          class="mx-auto"
          src="/empty-state.png"
          alt="Empty State"
          width="250"
        />
        <p class="text-gray-500">
          Click on a pollutant card to view details
        </p>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'PollutantSummary',

  props: {
    noPollutantEmissions: {
      type: Boolean,
      required: true,
    },
    pollutant: {
      type: Object,
      required: true,
    },
    pollutants: {
      type: Array,
      required: true,
    },
    emissions: {
      type: Number,
      required: true,
    },
    pollutantTrend: {
      type: String,
      required: true,
    },
    trends: {
      type: Object,
      required: true,
    },
  },

  data() {
    const monthsList = [...months()].map(({ name }) => name)
    const pollutantTrendLabels = monthsList
    const currentMonth = this.$moment().format('MMMM')

    while (pollutantTrendLabels[11] !== currentMonth) {
      pollutantTrendLabels.unshift(pollutantTrendLabels.pop())
    }
    return {
      monthsList,
      noEmissions: this.noPollutantEmissions,
      selectedPollutant: this.pollutant,
      selectedPollutants: this.pollutants,
      footprintPercentage: 0,
      totalEmissions: this.emissions,
      showTrendList: false,
      selectedPollutantTrend: this.pollutantTrend,
      pollutantTrends: this.trends,
      defaultPollutantTrendChartOptions: {
        colors: ['#8BB700'],
        legend: {
          show: false,
        },
        grid: {
          show: false,
        },
        chart: {
          toolbar: {
            show: false,
          },
          type: 'area',
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'smooth',
        },
        labels: pollutantTrendLabels,
        xaxis: {
          labels: {
            show: false,
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        yaxis: {
          labels: {
            show: false,
          },
          axisBorder: {
            show: false,
          },
        },
      },
      defaultPollutantTrendChartSeries: [
        {
          name: 'Pollutant',
          data: [],
        },
      ],
    }
  },

  computed: {
    maxGWP() {
      return [...this.selectedPollutants].reduce((a, b) =>
        a.gwp > b.gwp ? a : b,
      ).gwp
    },
    formattedGwp() {
      if (!this.selectedPollutant) return 0
      const gwp = this.selectedPollutant.gwp
      return gwp.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    gwpPercentage() {
      if (!this.selectedPollutant) return 0
      return (this.selectedPollutant.gwp / this.maxGWP) * 100
    },
    pollutantTrendOptions() {
      return Object.keys(this.pollutantTrends)
    },
    pollutantTrendChartSeries() {
      const pollutant = this.selectedPollutant.chemical_formula
      let data = this.pollutantTrends[this.selectedPollutantTrend]?.find(
        ({ name }) => name === pollutant,
      ).data

      if (this.selectedPollutantTrend === 'monthly') {
        data = Array(4)
          .fill(0)
          .map((_, index) => {
            return [...data]
              .filter((_, dataIndex) => {
                return (
                  dataIndex < (index + 1) * 8
                  && dataIndex >= (index + 1) * 8 - 8
                )
              })
              .reduce((prev, next) => prev + next, 0)
          })
      }

      return [
        {
          name: pollutant,
          data,
        },
      ]
    },
    pollutantTrendChartOptions() {
      let labels = []
      if (this.selectedPollutantTrend === 'yearly') {
        const currentMonth = this.$moment().format('MMMM')
        labels = this.monthsList
        while (labels[11] !== currentMonth) {
          labels.unshift(labels.pop())
        }
      }
      else if (this.selectedPollutantTrend === 'monthly') {
        labels = ['3 weeks ago', '2 weeks ago', 'Previous week', 'This week']
      }
      else {
        labels = [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
          'Sunday',
        ]
        const currentDay = this.$moment().format('dddd')
        while (labels[6] !== currentDay) {
          labels.unshift(labels.pop())
        }
      }
      return {
        colors: ['#8BB700'],
        legend: {
          show: false,
        },
        grid: {
          show: false,
        },
        chart: {
          toolbar: {
            show: false,
          },
          type: 'area',
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'smooth',
        },
        labels,
        xaxis: {
          labels: {
            show: false,
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        yaxis: {
          labels: {
            show: false,
            formatter(val) {
              return thousands(val, 3)
            },
          },
          axisBorder: {
            show: false,
          },
        },
      }
    },
  },

  watch: {
    noPollutantEmissions(newCondition) {
      this.noEmissions = newCondition
    },
    emissions(newEmissions) {
      this.totalEmissions = newEmissions
    },
    pollutant(newPollutant) {
      this.selectedPollutant = newPollutant
      if (!this.noEmissions) {
        const pollutantTotal = Number(newPollutant.total_emissions_mtco2)
        this.footprintPercentage
          = pollutantTotal === 0
            ? 0
            : Number(((pollutantTotal / this.totalEmissions) * 100).toFixed(0))
      }
      this.updateTrendChart()
    },
    pollutants(newPollutants) {
      this.selectedPollutants = newPollutants
    },
    pollutantTrend(newTrend) {
      this.selectedPollutantTrend = newTrend
    },
    selectedPollutantTrend() {
      this.updateTrendChart()
    },
    trends(newTrends) {
      this.pollutantTrends = newTrends
    },
  },

  methods: {
    updateTrendChart() {
      if (!Object.keys(this.selectedPollutant).length) {
        return
      }

      nextTick(() => {
        this.$refs.pollutantTrendChart.updateOptions(
          this.pollutantTrendChartOptions,
          true,
          true,
        )
        this.$refs.pollutantTrendChart.updateSeries(
          this.pollutantTrendChartSeries,
          true,
          true,
        )
      })
    },
  },
}
</script>
